import { useMemo, useState, useRef, useEffect, createContext, useContext } from "react";
import { InRow } from "common/Grid";
import { Form, FormField as BaseFormField, PrimaryButton, SecondaryButton } from "common/Forms";
import { DateInput, FloatInput, IntegerInput, SelectInput, TextInput, TextAreaInput } from "common/Forms/inputs";
import { Grid, GridCell } from "common/Grid/Grid";
import { useNavigate } from "react-router-dom";
import { useGetSalesUsersListQuery } from "store/action/salesUserApi";
import { userRoles, SALES_LEAD_STATUS } from "Constant/Constant";
import { parseSalesLeadErrors } from "./salesLead.schema";
import { usePermissions } from "context/PermissionsContext";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";

const FormErrorContext = createContext({});

const FormField = (props) => {
  const errors = useContext(FormErrorContext);
  const errorKey = Object.keys(errors).find(
    (k) =>
      k.split(".").pop() === props.id ||
      k.split(".").pop().toLowerCase() === props.id?.toLowerCase() ||
      k === props.id,
  );
  const { inputFactory, ...rest } = props;
  const wrappedInputFactory = (id, name, onValidation) => (
    <div className="text-black">{inputFactory(id, name, onValidation)}</div>
  );
  return (
    <BaseFormField
      {...rest}
      inputFactory={wrappedInputFactory}
      errorMessage={errorKey ? errors[errorKey] : undefined}
    />
  );
};

const emptyObj = {};

// Normalize a date-ish value (Firestore {_seconds} timestamp or ISO string) into a Date for DateInput.
const toDateValue = (value) =>
  value?._seconds ? new Date(value._seconds * 1000) : value ? new Date(value) : undefined;

// Lead Temperature / Priority dropdown. Option keys are STRINGS so that the
// "—" option (key "0") is truthy and displays correctly in SelectInput, while
// the value stored in state and sent to the API stays a NUMBER (0 = none).
const LEAD_TEMPERATURE_OPTIONS = [
  { label: "Select Priority", value: "0" },
  { label: "Low", value: "1" },
  { label: "Medium", value: "2" },
  { label: "High", value: "3" },
];

export const SalesLeadForm = ({
  defaultValues = emptyObj,
  onSubmit,
  isLoading,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const textInputBoxClass = ["text_input_box"];

  const [userInput, setUserInput] = useState({
    companyId: defaultValues.companyId || "",
    name: defaultValues.name || "",
    contactId: defaultValues.contactId || "",
    salesRepresentative: defaultValues.salesRepresentative || "",
    email: defaultValues.email || "",
    phone: defaultValues.phone || "",
    phonePrefix: defaultValues.phonePrefix || "+61",
    address: defaultValues.address || "",
    industry: defaultValues.industry || "",
    currentPlatform: defaultValues.currentPlatform || "",
    website: defaultValues.website || "",
    estimatedAnnualAppSales: defaultValues.estimatedAnnualAppSales ?? "",
    estimatedMonthlyValue: defaultValues.estimatedMonthlyValue ?? "",
    estimatedCustomersPerDay: defaultValues.estimatedCustomersPerDay ?? "",
    estimatedConversionRate: defaultValues.estimatedConversionRate ?? "",
    avgSpend: defaultValues.avgSpend ?? "",
    proposedFee: defaultValues.proposedFee ?? "",
    estimatedAnnualAppFee: defaultValues.estimatedAnnualAppFee,
    assignedTo: defaultValues.assignedTo || "",
    status: defaultValues.status ?? (!isEdit ? SALES_LEAD_STATUS.COLD : undefined),
    leadTemperature: defaultValues.leadTemperature ?? 0,
    leadSource: defaultValues.leadSource || "",
    relationshipStatus: defaultValues.relationshipStatus || "",
    expectedCloseDate: toDateValue(defaultValues.expectedCloseDate),
    dateContacted: toDateValue(defaultValues.dateContacted),
    lastContactDate: toDateValue(defaultValues.lastContactDate),
    nextFollowUpDate: toDateValue(defaultValues.nextFollowUpDate),
    timesContacted: defaultValues.timesContacted ?? "",
    pipelineStage: defaultValues.pipelineStage || "",
    posPaymentOpportunity: defaultValues.posPaymentOpportunity || "",
    strategicPartnershipPotential: defaultValues.strategicPartnershipPotential || "",
    sourceWorkbook: defaultValues.sourceWorkbook || "",
    sourceTab: defaultValues.sourceTab || "",
    sourceRow: defaultValues.sourceRow ?? "",
    nextStep: defaultValues.nextStep || "",
    notes: defaultValues?.notes?.trim() || "",
    demoRejectionReason: defaultValues.demoRejectionReason || "",
    liveRejectionReason: defaultValues.liveRejectionReason || "",
  });

  const [prefixOpen, setPrefixOpen] = useState(false);
  const [prefixSearch, setPrefixSearch] = useState("");
  const prefixRef = useRef(null);
  const prefixSearchRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!prefixOpen) return;
    const handler = (e) => {
      if (prefixRef.current && !prefixRef.current.contains(e.target)) {
        setPrefixOpen(false);
        setPrefixSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [prefixOpen]);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (prefixOpen) {
      requestAnimationFrame(() => prefixSearchRef.current?.focus());
    }
  }, [prefixOpen]);

  const getFlagEmoji = (countryCode) =>
    countryCode
      .toUpperCase()
      .split("")
      .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
      .join("");

  const countryOptions = useMemo(() =>
    getCountries()
      .map((country) => ({
        code: country,
        name: en[country] || country,
        dialCode: `+${getCountryCallingCode(country)}`,
        flag: getFlagEmoji(country),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    []);

  const selectedCountryCode = useMemo(() => {
    if (!userInput.phonePrefix) return "";
    const match = countryOptions.find((c) => c.dialCode === userInput.phonePrefix);
    return match ? match.code : "";
  }, [userInput.phonePrefix, countryOptions]);

  const filteredPrefixOptions = useMemo(() => {
    if (!prefixSearch.trim()) return countryOptions;
    const q = prefixSearch.toLowerCase();
    return countryOptions.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q),
    );
  }, [prefixSearch, countryOptions]);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const { data: usersResponse } = useGetSalesUsersListQuery({
    isActive: true,
  });
  const { role, userId } = usePermissions();

  const userOptions = useMemo(() => {
    let users = usersResponse?.data || [];

    if (role === userRoles.SALES_PERSON) {
      if (isEdit) {
        users = users.filter((u) => u._id === userId || u._id === userInput.assignedTo);
      } else {
        users = users.filter((u) => u._id === userId);
      }
    }

    return users.map((u) => ({
      label: `${u.name} (${u.email})`,
      value: u._id,
    }));
  }, [usersResponse, role, userId, isEdit, userInput.assignedTo]);

  const NEW_LEAD_STATUSES = [SALES_LEAD_STATUS.COLD, SALES_LEAD_STATUS.WARM, SALES_LEAD_STATUS.HOT];

  const statusOptions = useMemo(() => {
    if (!isEdit) {
      return [
        { label: "Cold", value: SALES_LEAD_STATUS.COLD },
        { label: "Warm", value: SALES_LEAD_STATUS.WARM },
        { label: "Hot", value: SALES_LEAD_STATUS.HOT },
      ];
    }
    if (NEW_LEAD_STATUSES.includes(defaultValues.status)) {
      return [
        { label: "Cold", value: SALES_LEAD_STATUS.COLD },
        { label: "Warm", value: SALES_LEAD_STATUS.WARM },
        { label: "Hot", value: SALES_LEAD_STATUS.HOT },
        { label: "Completed", value: SALES_LEAD_STATUS.COMPLETED },
        { label: "Canceled", value: SALES_LEAD_STATUS.CANCELED }
      ];
    }
    return [
      { label: "Completed", value: SALES_LEAD_STATUS.COMPLETED },
      { label: "Canceled", value: SALES_LEAD_STATUS.CANCELED },
    ];
  }, [isEdit, defaultValues.status]);

  const formErrors = useMemo(() => {
    return isSubmitted ? parseSalesLeadErrors(userInput, { isEdit }) : {};
  }, [userInput, isSubmitted, isEdit]);

  const isFormValid = useMemo(() => {
    const errors = parseSalesLeadErrors(userInput, { isEdit });
    return Object.keys(errors).length === 0;
  }, [userInput, isEdit]);

  const handleValidatedSubmit = () => {
    setIsSubmitted(true);
    if (!isFormValid) {
      return;
    }
    onSubmit({
      ...userInput,
      status: userInput.status || defaultValues.status,
    });
  };

  return (
    <FormErrorContext.Provider value={formErrors}>
      <Form classNames={["my-6"]}>
        <Grid columns={2} rowGap={1.5} columnGap={2} classNames={["!gap-x-3 !gap-y-3"]}>
          <GridCell>
            <FormField
              id="companyId"
              label="Company ID"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Company ID"
                  value={userInput.companyId}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, companyId: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="name"
              label="Company Name"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter company name"
                  value={userInput.name}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, name: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="contactId"
              label="Contact ID"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Contact ID"
                  value={userInput.contactId}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, contactId: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="salesRepresentative"
              label="Contact Person"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter contact person"
                  value={userInput.salesRepresentative}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, salesRepresentative: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="email"
              label="Email"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter email"
                  value={userInput.email}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, email: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="phone"
              label="Phone"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => {
                const selected = selectedCountryCode
                  ? countryOptions.find((c) => c.code === selectedCountryCode)
                  : null;
                return (
                  <div style={{ display: "flex", alignItems: "stretch", width: "100%", position: "relative" }} ref={prefixRef}>
                    {/* ── Trigger button ── */}
                    <button
                      type="button"
                      id="phonePrefix"
                      onClick={() => { setPrefixOpen((o) => !o); setPrefixSearch(""); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexShrink: 0,
                        height: "39px",
                        padding: "0 10px",
                        border: "2px solid #e2e8f0",
                        borderRight: "none",
                        borderRadius: "4px 0 0 4px",
                        background: "#f8fafc",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        minWidth: "80px",
                        outline: "none",
                        userSelect: "none",
                      }}
                    >
                      <span style={{ fontSize: "18px", lineHeight: 1 }}>{selected ? selected.flag : "🌐"}</span>
                      <span style={{ fontSize: "14px" }}>{selected ? selected.dialCode : "e.g. +61"}</span>
                      <span style={{ fontSize: "9px", color: "#94a3b8", marginLeft: "2px" }}>{prefixOpen ? "▲" : "▼"}</span>
                    </button>

                    <div style={{ flex: 1, overflow: "hidden", borderRadius: "0 4px 4px 0" }}>
                      <TextInput
                        id={id}
                        name={name}
                        placeholder="Enter phone number"
                        value={userInput.phone}
                        onChange={(_, val) => {
                          const digits = val.replace(/\D/g, "");
                          setUserInput((prev) => ({ ...prev, phone: digits }));
                        }}
                      />
                    </div>

                    {/* ── Dropdown panel ── */}
                    {prefixOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "calc(100% + 4px)",
                          left: 0,
                          zIndex: 1000,
                          background: "#fff",
                          border: "1.5px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          width: "300px",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        {/* Search box */}
                        <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                          <input
                            ref={prefixSearchRef}
                            type="text"
                            placeholder="Search country or code…"
                            value={prefixSearch}
                            onChange={(e) => setPrefixSearch(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              border: "1.5px solid #e2e8f0",
                              borderRadius: "6px",
                              fontSize: "13px",
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        {/* Country list */}
                        <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                          {filteredPrefixOptions.length === 0 ? (
                            <div style={{ padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                              No country found
                            </div>
                          ) : (
                            filteredPrefixOptions.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => {
                                  setUserInput((prev) => ({ ...prev, phonePrefix: c.dialCode }));
                                  setPrefixOpen(false);
                                  setPrefixSearch("");
                                }}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  width: "100%",
                                  padding: "8px 12px",
                                  border: "none",
                                  background: selectedCountryCode === c.code ? "#f0f9ff" : "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  textAlign: "left",
                                  color: "#374151",
                                }}
                                onMouseEnter={(e) => { if (selectedCountryCode !== c.code) e.currentTarget.style.background = "#f8fafc"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = selectedCountryCode === c.code ? "#f0f9ff" : "transparent"; }}
                              >
                                <span style={{ fontSize: "18px", lineHeight: 1, flexShrink: 0 }}>{c.flag}</span>
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                                <span style={{ color: "#64748b", fontWeight: 600, flexShrink: 0 }}>{c.dialCode}</span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </GridCell>

          <GridCell columnSpan={1}>
            <FormField
              id="address"
              label="Address"
              required={true}
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter address"
                  value={userInput.address}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, address: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="industry"
              label="Industry / Category"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="e.g. Cafe, Retail, Hospitality"
                  value={userInput.industry}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, industry: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="currentPlatform"
              label="Current Platform"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="e.g. Shopify, WooCommerce"
                  value={userInput.currentPlatform}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, currentPlatform: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="website"
              label="Website"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="e.g. https://example.com"
                  value={userInput.website}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, website: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="estimatedAnnualAppSales"
              label="Estimated Annual App Sales"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <IntegerInput
                  id={id}
                  name={name}
                  placeholder="Enter estimated annual app sales"
                  value={userInput.estimatedAnnualAppSales}
                  min={1}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, estimatedAnnualAppSales: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="estimatedMonthlyValue"
              label="Estimated Monthly Value"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <IntegerInput
                  id={id}
                  name={name}
                  placeholder="Enter Estimated Monthly Value"
                  value={userInput.estimatedMonthlyValue}
                  min={0}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, estimatedMonthlyValue: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="estimatedCustomersPerDay"
              label="Estimated Customers / Day"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <IntegerInput
                  id={id}
                  name={name}
                  placeholder="Enter Estimated Customers / Day"
                  value={userInput.estimatedCustomersPerDay}
                  min={0}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, estimatedCustomersPerDay: val }))
                  }
                />
              )}
            />
          </GridCell>


          <GridCell>
            <FormField
              id="estimatedConversionRate"
              label="Estimated Conversion %"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="e.g. 0.4 for 40%"
                  value={userInput.estimatedConversionRate}
                  min={0}
                  max={1}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, estimatedConversionRate: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="avgSpend"
              label="Avg Spend"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="Enter Avg Spend"
                  value={userInput.avgSpend}
                  min={0}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, avgSpend: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="proposedFee"
              label="Proposed Fee (%)"
              type="number"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="Enter proposed fee"
                  value={userInput.proposedFee}
                  min={1}
                  max={100}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, proposedFee: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="estimatedAnnualAppFee"
              label="Estimated Annual App Fee"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <FloatInput
                  id={id}
                  name={name}
                  placeholder="Enter estimated annual app fee"
                  value={userInput.estimatedAnnualAppFee}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, estimatedAnnualAppFee: val }))
                  }
                  min={1}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="assignedTo"
              label="Assigned To"
              required={true}
              inputFactory={(id, name) => (
                <SelectInput
                  id={id}
                  name={name}
                  placeholder="Select user"
                  selectedKey={userInput.assignedTo}
                  optionProvider={() => userOptions}
                  keySelector={(x) => x.value}
                  labelSelector={(x) => x.label}
                  isClearable={true}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, assignedTo: n ? n.value : "" }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
              <FormField
                id="status"
                label="Status"
                required={!isEdit}
                inputFactory={(id, name) => (
                  <SelectInput
                    id={id}
                    name={name}
                    placeholder="Select status"
                    selectedKey={userInput.status}
                    optionProvider={() => statusOptions}
                    keySelector={(x) => x.value}
                    labelSelector={(x) => x.label}
                    isClearable={false}
                    onChange={(_, n) =>
                      setUserInput((prev) => ({ ...prev, status: n ? n.value : "" }))
                    }
                  />
                )}
            />
          </GridCell>

          
          <GridCell>
            <FormField
              id="leadTemperature"
              label="Lead Temperature / Priority"
              inputFactory={(id, name) => (
                <SelectInput
                  id={id}
                  name={name}
                  placeholder="Select priority"
                  selectedKey={String(userInput.leadTemperature ?? 0)}
                  optionProvider={() => LEAD_TEMPERATURE_OPTIONS}
                  keySelector={(x) => x.value}
                  labelSelector={(x) => x.label}
                  isClearable={false}
                  onChange={(_, n) =>
                    setUserInput((prev) => ({ ...prev, leadTemperature: n ? Number(n.value) : 0 }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="leadSource"
              label="Lead Source"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Lead Source"
                  value={userInput.leadSource}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, leadSource: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="relationshipStatus"
              label="Relationship Status"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Relationship Status"
                  value={userInput.relationshipStatus}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, relationshipStatus: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="expectedCloseDate"
              label="Expected Close Date"
              inputFactory={(id, name) => (
                <DateInput
                  id={id}
                  name={name}
                  disabled={false}
                  value={userInput.expectedCloseDate}
                  placeholder="Select Expected Close Date"
                  minDate={new Date()}
                  onChange={(_, n) => setUserInput((prev) => ({ ...prev, expectedCloseDate: n }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="dateContacted"
              label="Date Contacted"
              inputFactory={(id, name) => (
                <DateInput
                  id={id}
                  name={name}
                  disabled={false}
                  value={userInput.dateContacted}
                  placeholder="Select Date Contacted"
                  onChange={(_, n) => setUserInput((prev) => ({ ...prev, dateContacted: n }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="lastContactDate"
              label="Last Contact Date"
              inputFactory={(id, name) => (
                <DateInput
                  id={id}
                  name={name}
                  disabled={false}
                  value={userInput.lastContactDate}
                  placeholder="Select Last Contact Date"
                  onChange={(_, n) => setUserInput((prev) => ({ ...prev, lastContactDate: n }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="nextFollowUpDate"
              label="Next Follow-Up Date"
              inputFactory={(id, name) => (
                <DateInput
                  id={id}
                  name={name}
                  disabled={false}
                  value={userInput.nextFollowUpDate}
                  placeholder="Select Next Follow-Up Date"
                  minDate={new Date()}
                  onChange={(_, n) => setUserInput((prev) => ({ ...prev, nextFollowUpDate: n }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="timesContacted"
              label="Times Contacted"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <IntegerInput
                  id={id}
                  name={name}
                  placeholder="Enter Times Contacted"
                  value={userInput.timesContacted}
                  min={0}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, timesContacted: val }))
                  }
                />
              )}
            />
          </GridCell>


          <GridCell>
            <FormField
              id="pipelineStage"
              label="Pipeline Stage"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Pipeline Stage"
                  value={userInput.pipelineStage}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, pipelineStage: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="posPaymentOpportunity"
              label="POS / Payment Opportunity"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter POS / Payment Opportunity"
                  value={userInput.posPaymentOpportunity}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, posPaymentOpportunity: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="strategicPartnershipPotential"
              label="Strategic Partnership Potential"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Strategic Partnership Potential"
                  value={userInput.strategicPartnershipPotential}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, strategicPartnershipPotential: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="sourceWorkbook"
              label="Source Workbook"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Source Workbook"
                  value={userInput.sourceWorkbook}
                  onChange={(_, val) =>
                    setUserInput((prev) => ({ ...prev, sourceWorkbook: val }))
                  }
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="sourceTab"
              label="Source Tab"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter Source Tab"
                  value={userInput.sourceTab}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, sourceTab: val }))}
                />
              )}
            />
          </GridCell>

          <GridCell>
            <FormField
              id="sourceRow"
              label="Source Row"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <IntegerInput
                  id={id}
                  name={name}
                  placeholder="Enter Source Row"
                  value={userInput.sourceRow}
                  min={0}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, sourceRow: val }))}
                />
              )}
            />
          </GridCell>


          <GridCell>
            <FormField
              id="nextStep"
              label="Next Step"
              classNames={textInputBoxClass}
              inputFactory={(id, name) => (
                <TextInput
                  id={id}
                  name={name}
                  placeholder="Enter next step"
                  value={userInput.nextStep}
                  onChange={(_, val) => setUserInput((prev) => ({ ...prev, nextStep: val }))}
                />
              )}
            />
          </GridCell>

          {isEdit && userInput.demoRejectionReason && (
            <GridCell>
               <FormField
                 id="demoRejectionReason"
                 label="Reason for reject demo"
                 classNames={textInputBoxClass}
                 inputFactory={(id, name) => (
                   <TextAreaInput
                     id={id}
                     name={name}
                     value={userInput.demoRejectionReason}
                     disabled={true}
                     onChange={() => { }}
                   />
                 )}
               />
             </GridCell>
           )}

           {isEdit && userInput.liveRejectionReason && (
             <GridCell>
               <FormField
                 id="liveRejectionReason"
                 label="Reason for reject live"
                 classNames={textInputBoxClass}
                 inputFactory={(id, name) => (
                   <TextAreaInput
                     id={id}
                     name={name}
                     value={userInput.liveRejectionReason}
                     disabled={true}
                     onChange={() => { }}
                   />
                 )}
               />
             </GridCell>
           )}

           <GridCell
             columnSpan={
               isEdit && userInput.demoRejectionReason && userInput.liveRejectionReason
                 ? 2
                 : isEdit && (userInput.demoRejectionReason || userInput.liveRejectionReason)
                   ? 1
                   : 2
             }
           >
             <FormField
               id="notes"
               label="Notes"
               classNames={textInputBoxClass}
               inputFactory={(id, name) => (
                 <TextAreaInput
                   id={id}
                   name={name}
                   placeholder="Enter notes"
                   value={userInput.notes}
                   rows={2}
                   resizable={true}
                   onChange={(_, val) => setUserInput((prev) => ({ ...prev, notes: val }))}
                 />
               )}
             />
           </GridCell>
         </Grid>

         <InRow gap={1} classNames={["justify-center mt-8"]}>
           <PrimaryButton
             classNames={["min-w-[180px]"]}
             disabled={isLoading}
             action={handleValidatedSubmit}>
             {isEdit ? "Update Sales Lead" : "Create Sales Lead"}
           </PrimaryButton>
           <SecondaryButton classNames={["min-w-[180px]"]} action={() => navigate("/sales-leads")}>
             Cancel
           </SecondaryButton>
         </InRow>
       </Form>
     </FormErrorContext.Provider>
   );
};
